import * as React from 'react';
import {
  BookIcon,
  CuboidIcon,
  MessageSquareIcon,
  SendIcon,
  ShieldIcon
} from 'lucide-react';

import {
  APP_DISCORD_INVITE_URL,
  APP_GITHUB_REPO_URL
} from '@workspace/common/app';
import { routes } from '@workspace/routes';
import {
  DiscordIcon,
  GitHubIcon,
  LinkedInIcon,
  XIcon,
  YouTubeIcon
} from '@workspace/ui/components/brand-icons';

type MenuLink = {
  title: string;
  href: string;
  external: boolean;
};

type FooterLink = {
  name: string;
  href: string;
  external: boolean;
};

type SocialLink = {
  name: string;
  href: string;
  external: boolean;
  icon: React.JSX.Element;
};

export type CommunityLink = {
  name: string;
  handle: string;
  href: string | null;
  external: boolean;
  icon: React.JSX.Element;
};

type DocsLinkItem = {
  title: string;
  href: string;
  description: string;
  keywords?: string[];
  items: DocsLinkItem[];
};

type DocsLinkGroup = {
  title: string;
  icon: React.JSX.Element;
  items: DocsLinkItem[];
};

export const MENU_LINKS: MenuLink[] = [
  {
    title: 'About',
    href: routes.marketing.Story,
    external: false
  },
  {
    title: 'Thesis',
    href: '/thesis/rent-the-models-own-the-mind',
    external: false
  },
  {
    title: 'Features',
    href: `${routes.marketing.Index}#features`,
    external: false
  },
  {
    title: 'Changelog',
    href: routes.marketing.Changelog,
    external: false
  },
  {
    title: 'Roadmap',
    href: routes.marketing.Roadmap,
    external: false
  },
  {
    title: 'Docs',
    href: routes.marketing.Docs,
    external: false
  }
];

export const FOOTER_LINKS: FooterLink[] = [
  { name: 'About', href: routes.marketing.Story, external: false },
  {
    name: 'Thesis',
    href: '/thesis/rent-the-models-own-the-mind',
    external: false
  },
  {
    name: 'Features',
    href: `${routes.marketing.Index}#features`,
    external: false
  },
  { name: 'Changelog', href: routes.marketing.Changelog, external: false },
  { name: 'Roadmap', href: routes.marketing.Roadmap, external: false },
  { name: 'Docs', href: routes.marketing.Docs, external: false },
  { name: 'Contact', href: routes.marketing.Contact, external: false }
];

export const COMMUNITY_LINKS: CommunityLink[] = [
  {
    name: 'Discord',
    handle: 'Live community',
    href: APP_DISCORD_INVITE_URL,
    external: true,
    icon: <DiscordIcon className="size-4 shrink-0" />
  },
  {
    name: 'Telegram',
    handle: '@viventiumai',
    href: 'https://t.me/viventiumai',
    external: true,
    icon: <SendIcon className="size-4 shrink-0" />
  },
  {
    name: 'YouTube',
    handle: '@adrienbeyk',
    href: 'https://www.youtube.com/@adrienbeyk',
    external: true,
    icon: <YouTubeIcon className="size-4 shrink-0" />
  },
  {
    name: 'Twitter',
    handle: '@adrienbeyk',
    href: 'https://x.com/AdrienBeyk',
    external: true,
    icon: <XIcon className="size-4 shrink-0" />
  },
  {
    name: 'LinkedIn - Adrien',
    handle: 'linkedin.com/in/adrienbeyk',
    href: 'https://www.linkedin.com/in/adrienbeyk',
    external: true,
    icon: <LinkedInIcon className="size-4 shrink-0" />
  },
  {
    name: 'LinkedIn - Viventium',
    handle: 'company/viventiumai',
    href: 'https://www.linkedin.com/company/viventiumai/',
    external: true,
    icon: <LinkedInIcon className="size-4 shrink-0" />
  },
  {
    name: 'GitHub',
    handle: 'ProjectViventium',
    href: APP_GITHUB_REPO_URL,
    external: true,
    icon: <GitHubIcon className="size-4 shrink-0" />
  }
];

export const SOCIAL_LINKS: SocialLink[] = COMMUNITY_LINKS.flatMap((link) =>
  link.href
    ? [
        {
          name: link.name,
          href: link.href,
          external: link.external,
          icon: link.icon
        }
      ]
    : []
);

export const DOCS_LINKS: DocsLinkGroup[] = [
  {
    title: 'Start Here',
    icon: <CuboidIcon className="size-4 shrink-0 text-muted-foreground" />,
    items: [
      {
        title: 'What is Viventium',
        href: '/docs',
        description:
          'The plain-language overview: what Viventium is, why it exists, and where to start.',
        keywords: [
          'overview',
          'second brain',
          'brain inspired',
          'open source',
          'local first'
        ],
        items: []
      },
      {
        title: 'Install & Setup',
        href: '/docs/getting-started',
        description:
          'Current public install path, activation key requirement, startup commands, and Connected Accounts.',
        keywords: [
          'install',
          'setup',
          'groq',
          'connected accounts',
          'doctor',
          'start',
          'upgrade'
        ],
        items: []
      }
    ]
  },
  {
    title: 'Core Services',
    icon: (
      <MessageSquareIcon className="size-4 shrink-0 text-muted-foreground" />
    ),
    items: [
      {
        title: 'Core Services',
        href: '/docs/core-services',
        description:
          'The practical map of Viventium chat, voice, Telegram, scheduling, memory, Prompt Workbench, workspaces, and workflows.',
        keywords: [
          'core services',
          'service map',
          'librechat',
          'voice gateway',
          'livekit',
          'telegram',
          'scheduling cortex',
          'memory',
          'recall',
          'prompt workbench',
          'glasshive',
          'self-healing',
          'feature request',
          'bug report'
        ],
        items: []
      },
      {
        title: 'Voice, Chat & Messaging',
        href: '/docs/interfaces',
        description:
          'How the chat web app, real-time voice, Telegram, Wing Mode, Listen-Only Mode, and voice providers stay tied to one continuity layer.',
        keywords: [
          'voice',
          'chat',
          'librechat',
          'telegram',
          'telegram voice',
          'voice note',
          'voice reply',
          'wing mode',
          'listen-only',
          'listen only mode',
          'messaging',
          'whisper',
          'chatterbox',
          'cartesia',
          'sonic-3',
          'xai',
          'emotion',
          'laughter',
          'livekit',
          'speech',
          'local voice'
        ],
        items: []
      },
      {
        title: 'Prompt Workbench',
        href: '/docs/prompt-workbench',
        description:
          'The local prompt QA surface for source/live/evaluated prompts, drift review, evals, traces, and scheduled prompts.',
        keywords: [
          'prompt workbench',
          'prompt atlas',
          'prompt flow',
          'live drift',
          'draft review',
          'eval designer',
          'prompt traces',
          'scheduled prompts',
          'subconscious deep thought',
          'prompts-only',
          'monaco'
        ],
        items: []
      },
      {
        title: 'Self-Healing & Feature Requests',
        href: '/docs/self-healing-feature-requests',
        description:
          'AI-assisted local workflows for self-healing, feature requests, bug reports, approval gates, isolated worktrees, and QA evidence.',
        keywords: [
          'self-healing',
          'self healing',
          'heal',
          'feature request',
          'bug report',
          'report bug',
          'workflows',
          'glasshive workflows',
          'approval',
          'rca',
          'isolated worktree',
          'qa evidence',
          'open-artifacts'
        ],
        items: []
      }
    ]
  },
  {
    title: 'Foundations',
    icon: <BookIcon className="size-4 shrink-0 text-muted-foreground" />,
    items: [
      {
        title: 'Product Philosophy',
        href: '/docs/philosophy',
        description:
          'The belief, community, and core problems Viventium is designed to solve.',
        keywords: [
          'belief',
          'community',
          'vision',
          'neuroscience',
          'philosophy',
          'why',
          'mission',
          'second brain'
        ],
        items: []
      },
      {
        title: 'Architecture Overview',
        href: '/docs/architecture',
        description:
          'How all components connect — main agent, cortices, voice, memory, scheduling, and GlassHive.',
        keywords: [
          'architecture',
          'system map',
          'components',
          'diagram',
          'librechat',
          'livekit',
          'mcp',
          'fastapi',
          'config'
        ],
        items: []
      },
      {
        title: 'Brain-Inspired System',
        href: '/docs/concepts',
        description:
          'How one main assistant, background agents, and shared continuity fit together.',
        keywords: [
          'brain inspired',
          'frontal cortex',
          'cortices',
          'main ai',
          'system',
          'neuroscience',
          'selective attention'
        ],
        items: []
      },
      {
        title: 'Background Agents',
        href: '/docs/agents',
        description:
          'How activation, non-blocking execution, and background follow-through work.',
        keywords: [
          'agents',
          'cortices',
          'activation',
          'non blocking',
          'follow through',
          'threshold',
          'cooldown',
          'context parity'
        ],
        items: []
      },
      {
        title: 'Red Team Cortex',
        href: '/docs/red-team',
        description:
          'How Viventium challenges your thinking — proactive bias detection, weak assumptions, and independent analysis.',
        keywords: [
          'red team',
          'bias',
          'challenge',
          'assumptions',
          'yes man',
          'verification',
          'fact check',
          'blind spots'
        ],
        items: []
      },
      {
        title: 'Memory & Continuity',
        href: '/docs/memory',
        description:
          'How Viventium keeps durable facts, recent context, drafts, and project state useful over time.',
        keywords: [
          'memory',
          'continuity',
          'recall',
          'signals',
          'drafts',
          'working memory',
          'semantic',
          'episodic',
          'vector'
        ],
        items: []
      }
    ]
  },
  {
    title: 'Surfaces',
    icon: (
      <MessageSquareIcon className="size-4 shrink-0 text-muted-foreground" />
    ),
    items: [
      {
        title: 'Connected Workspaces',
        href: '/docs/integrations',
        description:
          'Google Workspace, Microsoft 365, files, and the other systems that feed context into Viventium.',
        keywords: [
          'gmail',
          'calendar',
          'drive',
          'docs',
          'sheets',
          'outlook',
          'm365'
        ],
        items: []
      },
      {
        title: 'Scheduling',
        href: '/docs/scheduling',
        description:
          'Morning briefings, reminders, research loops, and the Nothing to Add principle.',
        keywords: [
          'scheduling',
          'morning briefings',
          'reminders',
          'nta',
          'proactive',
          'nothing to add',
          'cron',
          'mcp server',
          'sqlite',
          'recurring'
        ],
        items: []
      },
      {
        title: 'Browser & Analysis',
        href: '/docs/browser-and-analysis',
        description:
          'Live web work, file analysis, research, and artifact generation when chat alone is not enough.',
        keywords: [
          'browser',
          'research',
          'web search',
          'files',
          'analysis',
          'spreadsheet',
          'searxng',
          'firecrawl',
          'crawl',
          'scrape'
        ],
        items: []
      },
      {
        title: 'Projects, Workers & GlassHive',
        href: '/docs/projects-workers',
        description:
          'The durable work model for resumable sandboxes, delegated work, and controlled execution.',
        keywords: [
          'glasshive',
          'workers',
          'projects',
          'sandbox',
          'codex',
          'claude code',
          'openclaw',
          'novnc',
          'fastapi',
          'takeover',
          'autonomous'
        ],
        items: []
      }
    ]
  },
  {
    title: 'Operate & Trust',
    icon: <ShieldIcon className="size-4 shrink-0 text-muted-foreground" />,
    items: [
      {
        title: 'Trust Model & Deployment',
        href: '/docs/deployment',
        description:
          'Deployment, privacy, ownership, cost control, and what the current public path really means.',
        keywords: [
          'deployment',
          'trust',
          'ownership',
          'privacy',
          'open source'
        ],
        items: []
      },
      {
        title: 'Troubleshooting',
        href: '/docs/troubleshooting',
        description:
          'Recovery steps for setup, runtime, and account issues on the local public path.',
        keywords: ['issue', 'fix', 'recovery', 'doctor', 'troubleshooting'],
        items: []
      }
    ]
  }
];
